import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X, Save, ChevronUp, ChevronDown, Camera, Star } from "lucide-react";
import PhotoUploader from "@/components/PhotoUploader";

const KINDS = [
  { key: "passfail", label: "Pass / Fail / N-A" },
  { key: "written", label: "Written Answer" },
  { key: "multiple_choice", label: "Multiple Choice" },
  { key: "rank", label: "Rank (Likert 1–5)" },
];

const newItem = () => ({
  title: "", instructions: "",
  kind: "passfail",
  required: true, photo_required: false,
  options: ["", ""],          // for multiple_choice
  multi_select: false,         // for multiple_choice
  scale: 5,                    // for rank
  example_photo_ids: [],       // NEW: good-vs-bad reference photos (iter23)
});

export default function ChecklistBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [form, setForm] = useState({
    name: "", description: "", team_ids: [], tags: [],
    items: [newItem()], default_destination_ids: [],
  });
  const [tagInput, setTagInput] = useState("");

  useEffect(() => {
    api.get("/teams").then((r) => setTeams(r.data)).catch(() => {});
    api.get("/destinations").then((r) => setDestinations(r.data)).catch(() => {});
    if (id) {
      api.get("/checklist-templates").then((r) => {
        const t = r.data.find((x) => x.id === id);
        if (t) setForm({
          name: t.name, description: t.description || "",
          team_ids: t.team_ids && t.team_ids.length ? t.team_ids : (t.team_id ? [t.team_id] : []),
          tags: t.tags || [],
          items: (t.items || []).map((it) => ({ ...newItem(), ...it, example_photo_ids: it.example_photo_ids || [] })),
          default_destination_ids: t.default_destination_ids || [],
        });
      });
    }
  }, [id]);

  const update = (i, k, v) => {
    const items = [...form.items];
    items[i] = { ...items[i], [k]: v };
    setForm({ ...form, items });
  };
  const updateOption = (i, oi, v) => {
    const items = [...form.items];
    const opts = [...(items[i].options || [])];
    opts[oi] = v;
    items[i] = { ...items[i], options: opts };
    setForm({ ...form, items });
  };
  const addOption = (i) => update(i, "options", [...(form.items[i].options || []), ""]);
  const removeOption = (i, oi) => update(i, "options", form.items[i].options.filter((_, idx) => idx !== oi));
  const addItem = () => setForm({ ...form, items: [...form.items, newItem()] });
  const removeItem = (i) => setForm({ ...form, items: form.items.filter((_, idx) => idx !== i) });
  const move = (i, dir) => {
    const items = [...form.items];
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    [items[i], items[j]] = [items[j], items[i]];
    setForm({ ...form, items });
  };
  const toggleDest = (did) =>
    setForm({ ...form, default_destination_ids: form.default_destination_ids.includes(did)
      ? form.default_destination_ids.filter((x) => x !== did)
      : [...form.default_destination_ids, did] });

  const save = async () => {
    if (!form.name) return toast.error("Name required");
    if (form.items.length === 0 || form.items.some((it) => !it.title)) return toast.error("All items need a title");
    for (const it of form.items) {
      if (it.kind === "multiple_choice" && (it.options || []).filter((o) => o.trim()).length < 2) {
        return toast.error(`"${it.title}" needs at least 2 multiple-choice options`);
      }
    }
    const payload = { ...form, team_id: form.team_ids[0] || null };
    if (id) { await api.patch(`/checklist-templates/${id}`, payload); toast.success("Saved"); }
    else { await api.post("/checklist-templates", payload); toast.success("Created"); }
    nav("/app/checklists");
  };

  // iter29 — safer delete: only available from the Edit page (not the card),
  // requires explicit two-step confirmation. Completed runs remain in the DB
  // for traceability — only the reusable template is removed.
  const deleteTemplate = async () => {
    if (!id) return;
    const name = form.name || "this template";
    if (!window.confirm(`Delete "${name}"?\n\nThis stops the template from being started as a NEW audit run.\nAll completed runs stay in the database for traceability — nothing signed is lost.`)) return;
    const typed = window.prompt(`Type DELETE (in caps) to confirm removing "${name}".`);
    if ((typed || "").trim() !== "DELETE") {
      toast.error("Delete cancelled — confirmation text did not match");
      return;
    }
    try {
      await api.delete(`/checklist-templates/${id}`);
      toast.success("Template deleted — completed runs preserved for traceability");
      nav("/app/checklists");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="bg-white border border-[#1a1a1a]/15 p-5 rounded-2xl">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Builder</div>
        <h2 className="font-display font-black text-2xl tracking-tight">{id ? "Edit template" : "New template"}</h2>

        <div className="mt-5 space-y-3">
          <div>
            <Lbl>Audit-level tags (surfaced on every run)</Lbl>
            <div className="flex gap-2">
              <input data-testid="builder-tag-input" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && tagInput.trim()) {
                    e.preventDefault();
                    if (!form.tags.includes(tagInput.trim())) setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
                    setTagInput("");
                  }
                }}
                placeholder="press Enter to add · e.g. sqf, gmp, food-safety"
                className="flex-1 bg-white border border-[#1a1a1a]/30 rounded-md px-3 py-2 text-sm" />
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="builder-tag-chips">
                {form.tags.map((t) => (
                  <span key={t} className="inline-flex items-center gap-1 bg-[#fff1ea] text-[#ff4500] text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full">
                    {t}
                    <button onClick={() => setForm({ ...form, tags: form.tags.filter((x) => x !== t) })}><X className="w-3 h-3" /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div>
            <Lbl>Name</Lbl>
            <input data-testid="builder-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/30 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#1a1a1a]" />
          </div>
          <div>
            <Lbl>Description</Lbl>
            <textarea data-testid="builder-desc-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full bg-white border border-[#1a1a1a]/30 rounded-md px-3 py-2 text-sm h-20" />
          </div>
          <div>
            <Lbl>Share with hubs — pick as many as needed</Lbl>
            <div className="flex flex-wrap gap-1.5 max-h-40 overflow-auto p-2 bg-[#f7f5f2] rounded-md border border-[#1a1a1a]/10" data-testid="builder-hub-picker">
              {teams.length === 0 && <span className="text-[11px] text-[#1a1a1a]/55">Join a hub first to enable sharing.</span>}
              {teams.map((t) => {
                const on = form.team_ids.includes(t.id);
                return (
                  <button key={t.id} type="button" data-testid={`builder-hub-${t.id}`}
                    onClick={() => setForm({
                      ...form,
                      team_ids: on ? form.team_ids.filter((x) => x !== t.id) : [...form.team_ids, t.id],
                    })}
                    className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20 hover:border-[#ff4500]"}`}>
                    {t.name}
                  </button>
                );
              })}
            </div>
            {form.team_ids.length === 0 && <div className="text-[10px] text-[#1a1a1a]/55 mt-1">Nothing selected → template is private to you.</div>}
          </div>
          <div>
            <Lbl>Default destinations (where completed forms route)</Lbl>
            {destinations.length === 0 ? (
              <div className="text-[11px] text-[#1a1a1a]/55 bg-[#f7f5f2] rounded-md px-2 py-2">
                No destinations configured yet. Ask an Admin to add some on the <b>Admin · Destinations</b> page; you can come back and pick them after.
              </div>
            ) : (
              <div className="flex flex-wrap gap-1.5" data-testid="builder-destinations">
                {destinations.map((d) => {
                  const on = form.default_destination_ids.includes(d.id);
                  return (
                    <button key={d.id} type="button" data-testid={`builder-dest-${d.id}`} onClick={() => toggleDest(d.id)}
                      className={`text-[11px] uppercase tracking-wider font-bold px-2.5 py-1 rounded-full border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/15 hover:border-[#ff4500]"}`}>
                      {d.name} <span className="opacity-70">· {d.type}</span>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 mt-1">Anyone running this checklist will see these pre-selected on close-out — they can still add or remove.</div>
          </div>
        </div>

        <div className="mt-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500]">// Items</div>
          <ul className="mt-3 space-y-3">
            {form.items.map((it, i) => (
              <li key={i} className="border border-[#1a1a1a]/15 rounded-xl p-3 bg-[#f7f5f2]" data-testid={`item-card-${i}`}>
                <div className="flex items-start gap-2">
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} className="text-[#1a1a1a]/60 hover:text-[#1a1a1a]"><ChevronUp className="w-4 h-4" /></button>
                    <button onClick={() => move(i, 1)} className="text-[#1a1a1a]/60 hover:text-[#1a1a1a]"><ChevronDown className="w-4 h-4" /></button>
                  </div>
                  <div className="flex-1 space-y-2">
                    <input data-testid={`item-title-${i}`} value={it.title} onChange={(e) => update(i, "title", e.target.value)}
                      placeholder={`Question ${i + 1}`}
                      className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm font-display font-bold focus:outline-none focus:border-[#1a1a1a]" />
                    <input data-testid={`item-instructions-${i}`} value={it.instructions || ""} onChange={(e) => update(i, "instructions", e.target.value)}
                      placeholder="Helper / instructions (optional)"
                      className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-xs" />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      <div>
                        <Lbl>Question type</Lbl>
                        <select data-testid={`item-kind-${i}`} value={it.kind} onChange={(e) => update(i, "kind", e.target.value)}
                          className="w-full bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1.5 text-sm">
                          {KINDS.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                        </select>
                      </div>
                      <div className="flex items-end gap-3">
                        <Toggle tid={`item-required-${i}`} on={!!it.required} onChange={(v) => update(i, "required", v)} label="Mandatory" />
                        <Toggle tid={`item-photo-${i}`} on={!!it.photo_required} onChange={(v) => update(i, "photo_required", v)} label={<span className="inline-flex items-center gap-1"><Camera className="w-3 h-3" /> Photo required</span>} />
                      </div>
                    </div>
                    {it.kind === "multiple_choice" && (
                      <div className="bg-white border border-[#1a1a1a]/10 rounded-md p-2 space-y-1.5">
                        <div className="flex items-center justify-between">
                          <Lbl>Options</Lbl>
                          <Toggle tid={`item-multi-${i}`} on={!!it.multi_select} onChange={(v) => update(i, "multi_select", v)} label="Allow multiple selections" />
                        </div>
                        {(it.options || []).map((o, oi) => (
                          <div key={oi} className="flex items-center gap-2">
                            <input data-testid={`item-${i}-opt-${oi}`} value={o} onChange={(e) => updateOption(i, oi, e.target.value)}
                              placeholder={`Option ${oi + 1}`}
                              className="flex-1 bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
                            <button data-testid={`item-${i}-opt-rm-${oi}`} onClick={() => removeOption(i, oi)} className="text-[#ff4500]/70 hover:text-[#ff4500]"><X className="w-3 h-3" /></button>
                          </div>
                        ))}
                        <button data-testid={`item-${i}-opt-add`} onClick={() => addOption(i)} className="text-[11px] font-bold uppercase tracking-wider text-[#ff4500]">+ Add option</button>
                      </div>
                    )}
                    {it.kind === "rank" && (
                      <div className="bg-white border border-[#1a1a1a]/10 rounded-md p-2 flex items-center gap-3">
                        <Star className="w-3.5 h-3.5 text-[#ff4500]" />
                        <Lbl>Scale (1–N)</Lbl>
                        <input data-testid={`item-scale-${i}`} type="number" min="3" max="10" value={it.scale || 5}
                          onChange={(e) => update(i, "scale", Math.max(3, Math.min(10, Number(e.target.value))))}
                          className="w-20 bg-white border border-[#1a1a1a]/15 rounded-md px-2 py-1 text-sm" />
                      </div>
                    )}
                    {/* Example / reference photos — 'Good example' vs 'Bad example' style */}
                    <div className="bg-white border border-[#1a1a1a]/10 rounded-md p-2">
                      <Lbl>Example reference photos (shown next to the question during audit)</Lbl>
                      <div data-testid={`item-example-photos-${i}`}>
                        <PhotoUploader photoIds={it.example_photo_ids || []}
                          onChange={(ids) => update(i, "example_photo_ids", ids)}
                          testIdPrefix={`item-${i}-example`} />
                      </div>
                      <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/45 mt-1">
                        Great for &quot;good example vs bad example&quot; visual references.
                      </div>
                    </div>
                  </div>
                  <button data-testid={`remove-item-${i}`} onClick={() => removeItem(i)} className="text-[#ff4500] hover:bg-[#ff4500] hover:text-white p-1 rounded"><X className="w-4 h-4" /></button>
                </div>
              </li>
            ))}
          </ul>
          {/* Add-item button at BOTTOM of the list (per user request) */}
          <button data-testid="add-item-button" onClick={addItem}
            className="mt-3 w-full inline-flex items-center justify-center gap-1 border-2 border-dashed border-[#1a1a1a]/30 hover:border-[#ff4500] hover:bg-[#fff1ea] text-[#1a1a1a] px-3 py-3 rounded-xl text-[11px] uppercase tracking-wider font-bold transition-colors duration-150">
            <Plus className="w-3 h-3" /> New Question
          </button>
        </div>

        <div className="mt-6 flex gap-2 flex-wrap">
          <button data-testid="save-template-button" onClick={save}
            className="inline-flex items-center gap-2 bg-[#ff4500] text-white px-4 py-2 rounded-md text-xs uppercase tracking-wider font-bold hover:bg-[#cc3500]"><Save className="w-3.5 h-3.5" /> Save template</button>
          <button data-testid="cancel-template-button" onClick={() => nav("/app/checklists")}
            className="border border-[#1a1a1a] text-[#1a1a1a] px-4 py-2 rounded-md text-xs uppercase tracking-wider font-bold hover:bg-[#1a1a1a] hover:text-white transition-colors">Cancel</button>
          {id && (
            <button data-testid="delete-template-button" onClick={deleteTemplate}
              className="ml-auto border border-[#ff4500] text-[#ff4500] px-4 py-2 rounded-md text-xs uppercase tracking-wider font-bold hover:bg-[#ff4500] hover:text-white transition-colors inline-flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Delete template
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-[#1a1a1a]/15 p-5 rounded-2xl">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] mb-1">// Live preview</div>
        <h3 className="font-display font-black text-2xl tracking-tight">{form.name || "Untitled checklist"}</h3>
        {form.description && <p className="text-sm text-[#1a1a1a]/80 mt-2">{form.description}</p>}
        <ol className="mt-5 space-y-3">
          {form.items.map((it, i) => (
            <li key={i} className="border border-[#1a1a1a]/08 rounded-md p-3">
              <div className="font-display font-bold text-sm flex items-center gap-2 flex-wrap">
                <span className="font-mono text-[#ff4500] text-xs">{String(i + 1).padStart(2, "0")}</span>
                {it.title || <span className="text-[#1a1a1a]/40">(no title)</span>}
                {it.required && <span className="bg-[#ff4500] text-white text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider">Req</span>}
                {it.photo_required && <span className="bg-[#1a1a1a] text-white text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider inline-flex items-center gap-0.5"><Camera className="w-2.5 h-2.5" /> Photo</span>}
                <span className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/55 ml-auto">{KINDS.find((k) => k.key === it.kind)?.label}</span>
              </div>
              {it.instructions && <div className="text-[11px] text-[#1a1a1a]/70 mt-1">{it.instructions}</div>}
              {it.kind === "passfail" && <div className="mt-2 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">[ Pass ] [ Fail ] [ N-A ]</div>}
              {it.kind === "written" && <div className="mt-2 h-10 bg-[#f7f5f2] rounded border border-dashed border-[#1a1a1a]/15" />}
              {it.kind === "multiple_choice" && (
                <ul className="mt-2 space-y-0.5">{(it.options || []).filter((o) => o).map((o, oi) => (<li key={oi} className="text-[12px]">{it.multi_select ? "☐" : "○"} {o}</li>))}</ul>
              )}
              {it.kind === "rank" && (
                <div className="mt-2 flex gap-1">{Array.from({ length: it.scale || 5 }, (_, n) => <span key={n} className="w-7 h-7 rounded-full border border-[#1a1a1a]/15 flex items-center justify-center text-xs font-bold">{n + 1}</span>)}</div>
              )}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function Lbl({ children }) {
  return <label className="block text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/65 mb-1 font-mono">{children}</label>;
}
function Toggle({ tid, on, onChange, label }) {
  return (
    <label className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-bold text-[#1a1a1a]">
      <input data-testid={tid} type="checkbox" checked={on} onChange={(e) => onChange(e.target.checked)} className="accent-[#ff4500]" />
      {label}
    </label>
  );
}
